/**
 * Token Refresh Utility for AWS Cognito
 *
 * Implements secure token refresh following AWS Cognito best practices
 * with activity-based conditional refresh logic.
 */

export interface TokenResponse {
  access_token: string;
  id_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string; // Optional for refresh token rotation
}

export interface TokenRefreshConfig {
  clientId: string;
  tokenEndpoint: string;
}

export class TokenRefreshError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly response?: unknown
  ) {
    super(message);
    this.name = 'TokenRefreshError';
  }
}

/**
 * Get Cognito token endpoint based on environment
 */
export const getTokenEndpoint = (): string => {
  const isLocalStack = import.meta.env.REACT_APP_ENVIRONMENT_TYPE === 'local';

  if (isLocalStack) {
    return 'http://localhost:4566/_aws/cognito-idp/oauth2/token';
  } else {
    const domainPrefix = import.meta.env.REACT_APP_COGNITO_DOMAIN_PREFIX;
    const region = import.meta.env.REACT_APP_REGION;
    return `https://${domainPrefix}.auth.${region}.amazoncognito.com/oauth2/token`;
  }
};

/**
 * Get client ID from environment
 */
export const getClientId = (): string => {
  const clientId = import.meta.env.REACT_APP_USER_POOL_WEB_CLIENT_ID;
  if (!clientId) {
    throw new TokenRefreshError('Client ID not configured');
  }
  return clientId;
};

/**
 * Refresh access token using refresh token
 */
export const refreshAccessToken = async (): Promise<TokenResponse> => {
  const refreshToken = sessionStorage.getItem('refresh_token');
  if (!refreshToken) {
    throw new TokenRefreshError('No refresh token available');
  }

  const tokenEndpoint = getTokenEndpoint();
  const clientId = getClientId();

  const params = new URLSearchParams();
  params.append('grant_type', 'refresh_token');
  params.append('client_id', clientId);
  params.append('refresh_token', refreshToken);

  try {
    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new TokenRefreshError(
        `Token refresh failed: ${response.status} ${response.statusText}`,
        response.status,
        errorData
      );
    }

    const tokenData = await response.json();

    // Validate required fields
    if (!tokenData.access_token || !tokenData.id_token) {
      throw new TokenRefreshError('Invalid token response: missing required tokens');
    }

    return tokenData as TokenResponse;
  } catch (error) {
    if (error instanceof TokenRefreshError) {
      throw error;
    }

    // Network or other errors
    throw new TokenRefreshError(
      `Network error during token refresh: ${error instanceof Error ? error.message : 'Unknown error'}`,
      undefined,
      error
    );
  }
};

/**
 * Parse JWT token to extract expiration time
 */
export const parseJwtExpiration = (token: string): number => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }

    const payload = JSON.parse(atob(parts[1]));
    if (!payload.exp) {
      throw new Error('Token does not contain expiration claim');
    }

    // Convert from seconds to milliseconds
    return payload.exp * 1000;
  } catch (error) {
    throw new TokenRefreshError(
      `Failed to parse token expiration: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
};

/**
 * Check if token is expiring soon
 */
export const isTokenExpiringSoon = (
  token: string,
  bufferTimeMs: number = 5 * 60 * 1000
): boolean => {
  try {
    const expirationTime = parseJwtExpiration(token);
    const currentTime = Date.now();
    const timeUntilExpiry = expirationTime - currentTime;

    return timeUntilExpiry <= bufferTimeMs;
  } catch (error) {
    // If we can't parse the token, assume it's expiring
    console.warn('Unable to parse token expiration, assuming token is expiring:', error);
    return true;
  }
};

/**
 * Get token expiration time in milliseconds
 */
export const getTokenExpirationTime = (token: string): number => {
  return parseJwtExpiration(token);
};

/**
 * Store new tokens securely
 */
export const storeTokens = (tokenResponse: TokenResponse): void => {
  sessionStorage.setItem('access_token', tokenResponse.access_token);
  sessionStorage.setItem('id_token', tokenResponse.id_token);

  // Store new refresh token if provided (refresh token rotation)
  if (tokenResponse.refresh_token) {
    sessionStorage.setItem('refresh_token', tokenResponse.refresh_token);
  }
};

/**
 * Clear all stored tokens
 */
export const clearTokens = (): void => {
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('id_token');
  sessionStorage.removeItem('refresh_token');
};
